// Analytics central — consent-first (RGPD + nLPD/FADP suíço).
// REGRA: nenhum script de marketing carrega e nenhum evento é enviado sem consentimento.
// IDs lidos APENAS de variáveis de ambiente — nunca hardcoded:
//   REACT_APP_GTM_CONTAINER_ID, REACT_APP_GA4_MEASUREMENT_ID, REACT_APP_META_PIXEL_ID
//   (backend: META_PIXEL_ID, META_CONVERSIONS_ACCESS_TOKEN)
// Eventos Meta (CAPI + Pixel, com eventID partilhado p/ deduplicação):
//   PageView, ViewContent, Search, Contact, Lead, InitiateCheckout (+ AddPaymentInfo, Purchase, CompleteRegistration prontos)
// Eventos GA4 (dataLayer/gtag): page_view, buy_ticket_click, begin_checkout, contact_form_sent,
//   language_change, social_link_click, outbound_link, file_download, scroll_depth, search
//   (newsletter_signup / purchase_completed ficam prontos — sem formulário de newsletter nem checkout no site)

const API = process.env.REACT_APP_BACKEND_URL;
const GTM_ID = process.env.REACT_APP_GTM_CONTAINER_ID;
const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

const CONSENT_KEY = "fam_consent_v1";
const CONSENT_TTL_MS = 365 * 24 * 3600 * 1000; // 12 meses
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const META_EVENTS = new Set([
    "PageView", "ViewContent", "Search", "Contact", "Lead",
    "InitiateCheckout", "AddPaymentInfo", "Purchase", "CompleteRegistration",
]);

let initialized = false;
let listenersAttached = false;
let scrollMarks = [];

// ---------- Consentimento ----------

export const getConsent = () => {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        const c = JSON.parse(raw);
        if (Date.now() - c.ts > CONSENT_TTL_MS) return null;
        return c.marketing === true;
    } catch {
        return null;
    }
};

// Consent Mode v2 — defaults "denied" registados SEMPRE (sinais cookieless), antes de qualquer script
export const initConsentDefaults = () => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        wait_for_update: 500,
    });
};

export const grantConsent = (accepted) => {
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify({ marketing: accepted, ts: Date.now() }));
    } catch { /* sem storage */ }
    if (window.gtag) {
        window.gtag("consent", "update", {
            ad_storage: accepted ? "granted" : "denied",
            analytics_storage: accepted ? "granted" : "denied",
            ad_user_data: accepted ? "granted" : "denied",
            ad_personalization: accepted ? "granted" : "denied",
        });
    }
    if (accepted) {
        initAnalytics();
        trackPageView();
    }
};

// ---------- UTM / contexto ----------

const captureUtm = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const found = {};
        UTM_KEYS.forEach((k) => {
            const v = params.get(k);
            if (v) found[k] = v;
        });
        if (Object.keys(found).length > 0) {
            sessionStorage.setItem("fam_utm", JSON.stringify(found));
        }
        const fbclid = params.get("fbclid");
        if (fbclid) {
            sessionStorage.setItem("fam_fbc", `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`);
        }
    } catch { /* sem storage disponível */ }
};

export const getUtm = () => {
    try {
        return JSON.parse(sessionStorage.getItem("fam_utm") || "{}");
    } catch {
        return {};
    }
};

const pageType = () => {
    const p = window.location.pathname;
    if (p === "/" || p === "") return "home";
    if (p.startsWith("/bilhetes")) return "ticketing";
    if (p.startsWith("/admin")) return "admin";
    if (p.startsWith("/noticias")) return "news";
    return "subpage";
};

const pageLanguage = () => {
    try {
        return localStorage.getItem("fam-lang") || new URLSearchParams(window.location.search).get("lang") || "pt";
    } catch {
        return "pt";
    }
};

// ---------- Carregamento de scripts (só após consentimento) ----------

const injectScript = (src) => {
    const s = document.createElement("script");
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
};

export const initAnalytics = () => {
    if (initialized || getConsent() !== true) return;
    initialized = true;
    captureUtm();
    window.dataLayer = window.dataLayer || [];

    // GTM carrega sempre que houver contentor (gestão central de tags / dataLayer).
    // O GA4 também carrega diretamente enquanto o contentor GTM não tiver a tag GA4 configurada —
    // assim nunca há perda de dados. Quando a tag GA4 for criada dentro do GTM, remover o ramo direto.
    if (GTM_ID) {
        window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
        injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
    }
    if (GA4_ID) {
        injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
        window.gtag("js", new Date());
        window.gtag("config", GA4_ID, { send_page_view: false }); // page_view manual (SPA)
    }

    if (PIXEL_ID && !window.fbq) {
        const n = (window.fbq = function () {
            if (n.callMethod) n.callMethod.apply(n, arguments);
            else n.queue.push(arguments);
        });
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        injectScript("https://connect.facebook.net/en_US/fbevents.js");
        window.fbq("init", PIXEL_ID);
    }

    attachListeners();
};

// ---------- Listeners globais (GA4: language_change, social_link_click, outbound_link, file_download, scroll_depth) ----------

const attachListeners = () => {
    if (listenersAttached) return;
    listenersAttached = true;

    document.addEventListener("click", (e) => {
        const langBtn = e.target.closest?.('[data-testid*="lang-"]');
        if (langBtn) {
            const m = (langBtn.getAttribute("data-testid") || "").match(/lang-(pt|de|fr|it|en)$/);
            if (m) track("language_change", { language: m[1] });
        }
        const a = e.target.closest?.("a[href]");
        if (!a) return;
        const href = a.getAttribute("href") || "";
        if (/\.pdf($|[?#])/i.test(href) || a.hasAttribute("download")) {
            track("file_download", { file_url: href });
        } else if (/instagram\.com|facebook\.com/.test(href)) {
            track("social_link_click", { link_url: href });
        } else if (/^https?:\/\//.test(href) && !href.includes(window.location.host)) {
            track("outbound_link", { link_url: href });
        }
    });

    window.addEventListener("scroll", () => {
        const d = document.documentElement;
        if (!d.scrollHeight) return;
        const pct = Math.round(((window.scrollY + window.innerHeight) / d.scrollHeight) * 100);
        [25, 50, 75, 100].forEach((mark) => {
            if (pct >= mark && !scrollMarks.includes(mark)) {
                scrollMarks.push(mark);
                track("scroll_depth", { percent: mark });
            }
        });
    }, { passive: true });
};

// ---------- Núcleo ----------

// identity: dados de correspondência para a CAPI (ex.: { email }) — hash SHA-256 no servidor
export const track = (eventName, data = {}, identity = {}) => {
    if (getConsent() !== true) return null; // RGPD/nLPD: sem consentimento, sem tracking
    const event_id = crypto.randomUUID();
    const utm = getUtm();
    const base = {
        page_type: pageType(),
        page_language: pageLanguage(),
        ...utm,
        ...data,
        page_location: window.location.href,
        page_title: document.title,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, event_id, ...base });

    // Meta: Pixel (browser) + CAPI (servidor) com o MESMO event_id — deduplicação
    if (META_EVENTS.has(eventName)) {
        if (window.fbq) {
            try {
                window.fbq("track", eventName, { ...utm, ...data }, { eventID: event_id });
            } catch { /* noop */ }
        }
        try {
            const fbc = sessionStorage.getItem("fam_fbc");
            fetch(`${API}/api/events`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                keepalive: true,
                body: JSON.stringify({
                    event_name: eventName,
                    event_id,
                    event_time: Math.floor(Date.now() / 1000),
                    event_source_url: window.location.href,
                    event_data: { ...utm, ...data },
                    ...(fbc ? { fbc } : {}),
                    ...identity,
                }),
            });
        } catch { /* a medição nunca pode quebrar o site */ }
    }

    // GA4 direto (enquanto o contentor GTM não tem a tag GA4 configurada)
    if (GA4_ID && window.gtag && initialized) {
        try {
            window.gtag("event", eventName === "PageView" ? "page_view" : eventName, { event_id, ...base });
        } catch { /* noop */ }
    }
    return event_id;
};

export const trackPageView = () => {
    scrollMarks = [];
    return track("PageView");
};

export const trackInitiateCheckout = (ticketUrl) => {
    const data = { ticket_url: ticketUrl, ticket_type: "Standard", ticket_price: 119.9, value: 119.9, currency: "CHF" };
    track("InitiateCheckout", data); // Meta
    track("buy_ticket_click", data); // GA4
};
